import { getCurrentReader } from '../auth/getCurrentReader';

const base = (process.env.REACT_APP_BOOK_RATINGS_API || '').replace(/\/$/, '');
const accountChanged = () => Object.assign(Error('Your reader account changed. Reload ratings before continuing.'), { code: 'READER_ACCOUNT_CHANGED' });
const summaries = new Map();
let nextPublicRead = 0;
const summaryKey = value => { const url = new URL(value); url.search = ''; url.hash = ''; return url.href.replace(/\/$/, ''); };
export const ratingsConfigured = () => Boolean(base);
export async function ratingReader() {
  let timer;
  try {
    return await Promise.race([getCurrentReader(), new Promise((_, reject) => {
      timer = setTimeout(() => reject(Error('Your reader session took too long. Please retry.')), 15000);
    })]);
  } finally { clearTimeout(timer); }
}
async function request(path, productUrl, reader, rating, signal) {
  if (!base) throw Error('Book ratings are not available yet.');
  const controller = new AbortController(), abort = () => controller.abort();
  signal?.addEventListener('abort', abort);
  if (signal?.aborted) abort();
  const timer = setTimeout(abort, 15000);
  try {
    let current;
    if (reader) {
      current = await ratingReader();
      if (!current?.idToken || current.readerSub !== reader.readerSub) throw accountChanged();
    }
    if (controller.signal.aborted) throw Error('Request cancelled.');
    const response = await fetch(`${base}/${path}${rating == null ? `?productUrl=${encodeURIComponent(productUrl)}` : ''}`, {
      method: rating == null ? 'GET' : 'PUT', signal: controller.signal,
      headers: { ...(current ? { Authorization: `Bearer ${current.idToken}` } : {}), ...(rating != null ? { 'Content-Type': 'application/json' } : {}) },
      ...(rating != null ? { body: JSON.stringify({ productUrl, rating }) } : {}),
    });
    const data = await response.json();
    if (!response.ok) {
      if ([401, 403].includes(response.status)) throw Object.assign(Error('Please sign in as a reader again to rate this book.'), { code: 'READER_ACCOUNT_CHANGED' });
      throw Object.assign(Error(data.message || 'Ratings could not be loaded.'), { status: response.status });
    }
    if (reader) {
      const after = await ratingReader();
      if (after?.readerSub !== reader.readerSub || !after?.idToken) throw accountChanged();
    }
    if (controller.signal.aborted) throw Error('Request cancelled.');
    return data;
  } catch (error) {
    if (error.name === 'AbortError') throw Error('The ratings request timed out. Reload before trying again.');
    throw error;
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}
async function readTotals(productUrl, signal) {
  let data;
  // Pace marketplace-card reads below the API's burst limit. Only public reads retry.
  for (let attempt = 0; attempt < 3; attempt++) {
    const delay = Math.max(0, nextPublicRead - Date.now()); nextPublicRead = Date.now() + delay + 160;
    if (delay) await new Promise(resolve => setTimeout(resolve, delay));
    try { data = await request('ratings', productUrl, null, null, signal); break; }
    catch (error) { if (error.status !== 429 || attempt === 2) throw error; await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1))); }
  }
  if (!Number.isSafeInteger(data.count) || data.count < 0 || (data.count === 0 ? data.average !== null : typeof data.average !== 'number' || !Number.isFinite(data.average) || data.average < 1 || data.average > 5)) throw Error('Ratings returned an unreadable result.');
  return { count: data.count, average: data.average };
}
export async function getBookRatings(productUrl, signal) {
  if (signal) return readTotals(productUrl, signal);
  const key = summaryKey(productUrl), previous = summaries.get(key);
  if (previous && previous.expires > Date.now()) return previous.promise;
  const entry = { expires: Infinity };
  entry.promise = readTotals(key).then(data => { entry.expires = Date.now() + 30000; return data; })
    .catch(error => { if (summaries.get(key) === entry) summaries.delete(key); throw error; });
  summaries.set(key, entry);
  return entry.promise;
}
export async function getMyRating(productUrl, reader, signal) {
  const data = await request('my-rating', productUrl, reader, null, signal);
  if (data.rating !== null && (!Number.isInteger(data.rating) || data.rating < 1 || data.rating > 5)) throw Error('Your rating could not be loaded.');
  return data.rating;
}
export async function saveBookRating(productUrl, reader, rating, signal) {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) throw Error('Choose a rating from 1 to 5.');
  const data = await request('my-rating', productUrl, reader, rating, signal);
  if (data.rating !== rating) throw Error('The save could not be confirmed. Reload ratings.');
  summaries.delete(summaryKey(productUrl));
  return data.rating;
}
